import React, { useState } from 'react';
import {
    Container, Paper, TextField, Button, Typography, Box, Alert, CircularProgress, Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { apiService } from '../services/api';
import { useTranslation } from 'react-i18next';

const schema = yup.object({
    email: yup.string().email('forgotPassword.emailInvalid').required('forgotPassword.emailRequired'),
});

type FormData = yup.InferType<typeof schema>;

const ForgotPasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string>('');

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({ resolver: yupResolver(schema) });

    const onSubmit = async (data: FormData) => {
        try {
            setIsLoading(true);
            setError('');
            await apiService.forgotPassword(data.email);
            setSubmitted(true);
        } catch (err: any) {
            if (err.response?.status === 400) {
                setError(err.response?.data?.message || t('forgotPassword.validationError'));
            } else {
                // 200, 404 e qualsiasi altro errore → stesso messaggio generico
                setSubmitted(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
                    <Typography component="h1" variant="h4" align="center" gutterBottom>
                        {t('forgotPassword.title')}
                    </Typography>

                    {submitted ? (
                        <Box textAlign="center">
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {t('forgotPassword.successMessage')}
                            </Alert>
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                <MuiLink component={Link} to="/login">
                                    {t('forgotPassword.backToLogin')}
                                </MuiLink>
                            </Typography>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('forgotPassword.description')}
                            </Typography>

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label={t('forgotPassword.emailLabel')}
                                autoComplete="email"
                                autoFocus
                                {...register('email')}
                                error={!!errors.email}
                                helperText={errors.email?.message ? t(errors.email.message as string) : ''}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={isLoading}
                            >
                                {isLoading ? <CircularProgress size={24} /> : t('forgotPassword.sendButton')}
                            </Button>

                            <Box textAlign="center">
                                <Typography variant="body2">
                                    <MuiLink component={Link} to="/login">
                                        {t('forgotPassword.backToLogin')}
                                    </MuiLink>
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default ForgotPasswordPage;
